/**
 * Project settings: who has access, and how.
 *
 * Access is granted per project. Removing someone here removes it immediately — there is
 * no directory to reconcile against.
 */

import { useState } from "react";

import type { Role } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { useProject } from "@/components/ProjectLayout";
import { Badge, Empty, Field, Loading, Notice, RelativeTime } from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";

const ROLES: Role[] = ["viewer", "pm", "engineer", "admin"];

const ROLE_MEANING: Record<Role, string> = {
  viewer: "See scripts, runs, and output",
  pm: "Everything a viewer can, plus trigger and schedule runs",
  engineer: "Everything a PM can, plus onboard scripts and build versions",
  admin: "Everything, plus access and the audit trail",
};

function Members() {
  const { project, can } = useProject();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  const { data, loading, reload } = useAsync(
    () => endpoints.members(project.slug),
    [project.slug],
  );

  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.upsertMember(project.slug, { email, role });
      setEmail("");
      reload();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId: string) => {
    setProblem(undefined);
    try {
      await endpoints.removeMember(project.slug, userId);
      reload();
    } catch (caught) {
      setProblem((caught as Error).message);
    }
  };

  if (loading) return <Loading rows={3} label="Loading members" />;
  const members = data ?? [];

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">People with access</span>
        <span className="muted" style={{ fontSize: 12.5 }}>
          {members.length} {members.length === 1 ? "person" : "people"}
        </span>
      </div>

      <div className="card__body stack" style={{ gap: 14 }}>
        {problem && <Notice tone="danger">{problem}</Notice>}

        <div className="table__scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>How</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.user_id}>
                  <td>
                    <div style={{ fontWeight: 550 }}>{member.display_name}</div>
                    <div className="faint" style={{ fontSize: 12 }}>
                      {member.email}
                    </div>
                  </td>
                  <td>
                    <Badge tone="accent">{member.role}</Badge>
                  </td>
                  <td className="muted">Granted by an administrator</td>
                  <td style={{ textAlign: "right" }}>
                    {can("admin") && (
                      <button
                        type="button"
                        className="button button--small button--danger"
                        onClick={() => remove(member.user_id)}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {can("admin") && (
          <>
            <hr className="divider" />
            <Notice tone="info">
              The person needs an account first. A platform administrator invites them,
              then you give them a role here.
            </Notice>
            <form className="row" style={{ gap: 10, alignItems: "flex-end" }} onSubmit={add}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <Field label="Email" htmlFor="member-email">
                  <input
                    id="member-email"
                    className="input"
                    type="email"
                    value={email}
                    required
                    placeholder="someone@appen.com"
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Field>
              </div>
              <div style={{ width: 160 }}>
                <Field label="Role" htmlFor="member-role">
                  <select
                    id="member-role"
                    className="select"
                    value={role}
                    onChange={(event) => setRole(event.target.value as Role)}
                  >
                    {ROLES.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <button type="submit" className="button" disabled={busy}>
                Add
              </button>
            </form>
            <div className="faint" style={{ fontSize: 12.5 }}>
              {ROLE_MEANING[role]}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Secrets() {
  const { project, can } = useProject();
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  const { data, loading, reload } = useAsync(
    () => endpoints.secrets(project.slug),
    [project.slug],
  );

  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.putSecret(project.slug, name, value);
      setName("");
      setValue("");
      reload();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (secretName: string) => {
    setProblem(undefined);
    try {
      await endpoints.removeSecret(project.slug, secretName);
      reload();
    } catch (caught) {
      setProblem((caught as Error).message);
    }
  };

  if (!can("viewer")) return null;
  if (loading) return <Loading rows={2} label="Loading secrets" />;
  const secrets = data ?? [];

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Secrets</span>
        <span className="muted" style={{ fontSize: 12.5 }}>
          What a manifest's secret_ref params pick from
        </span>
      </div>

      <div className="card__body stack" style={{ gap: 14 }}>
        {problem && <Notice tone="danger">{problem}</Notice>}

        <Notice tone="info">
          A value is written once and never shown again — not here, not in a run
          console, not in the audit trail. Only the name a script asks for is visible.
        </Notice>

        {secrets.length === 0 ? (
          <Empty title="No secrets yet" />
        ) : (
          <div className="table__scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Last set</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {secrets.map((secret) => (
                  <tr key={secret.id}>
                    <td className="mono">{secret.name}</td>
                    <td className="muted">
                      <RelativeTime iso={secret.updated_at} />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {can("admin") && (
                        <button
                          type="button"
                          className="button button--small button--danger"
                          onClick={() => remove(secret.name)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {can("admin") && (
          <>
            <hr className="divider" />
            <form className="row" style={{ gap: 10, alignItems: "flex-end" }} onSubmit={add}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <Field label="Name" htmlFor="secret-name">
                  <input
                    id="secret-name"
                    className="input mono"
                    value={name}
                    required
                    placeholder="customer-api-key"
                    onChange={(event) => setName(event.target.value)}
                  />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <Field label="Value" htmlFor="secret-value">
                  <input
                    id="secret-value"
                    className="input"
                    type="password"
                    value={value}
                    required
                    onChange={(event) => setValue(event.target.value)}
                  />
                </Field>
              </div>
              <button type="submit" className="button" disabled={busy}>
                {busy ? "Saving…" : "Save"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function AuditTrail() {
  const { project, can } = useProject();

  const { data, loading } = useAsync(
    () => endpoints.audit(project.slug, { limit: 50 }),
    [project.slug],
    { enabled: can("admin") },
  );

  if (!can("admin")) return null;
  if (loading) return <Loading rows={3} label="Loading audit trail" />;

  const events = data?.items ?? [];

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Audit trail</span>
        <span className="muted" style={{ fontSize: 12.5 }}>
          Append-only. Outlives the data it describes.
        </span>
      </div>

      {events.length === 0 ? (
        <Empty title="Nothing recorded yet" />
      ) : (
        <div className="table__scroll">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>What</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td className="muted">
                    <RelativeTime iso={event.created_at} />
                  </td>
                  <td className="muted truncate" style={{ maxWidth: 190 }}>
                    {event.actor_email ?? <span className="faint">the platform</span>}
                  </td>
                  <td>
                    <span className="mono">{event.action.replace(/_/g, " ")}</span>
                    {event.detail && (
                      <div className="faint truncate" style={{ maxWidth: 300, fontSize: 11.5 }}>
                        {JSON.stringify(event.detail)}
                      </div>
                    )}
                  </td>
                  <td className="muted truncate" style={{ maxWidth: 240 }}>
                    {event.reason ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProjectSettings() {
  const { project, can, reload } = useProject();
  const editable = can("admin");

  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? "");
  const [isPii, setIsPii] = useState(project.is_pii);
  const [retention, setRetention] = useState(project.artifact_retention_days);
  const [concurrent, setConcurrent] = useState(project.max_concurrent_runs);
  const [schedules, setSchedules] = useState(project.max_schedules);
  const [notifications, setNotifications] = useState(project.notifications_enabled);

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();
  const [saved, setSaved] = useState(false);

  const dirty =
    name !== project.name ||
    description !== (project.description ?? "") ||
    isPii !== project.is_pii ||
    retention !== project.artifact_retention_days ||
    concurrent !== project.max_concurrent_runs ||
    schedules !== project.max_schedules ||
    notifications !== project.notifications_enabled;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    setSaved(false);
    try {
      await endpoints.updateProject(project.slug, {
        name,
        description: description || null,
        is_pii: isPii,
        artifact_retention_days: retention,
        max_concurrent_runs: concurrent,
        max_schedules: schedules,
        notifications_enabled: notifications,
      });
      setSaved(true);
      reload();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <div className="card__header">
        <span className="card__title">This project</span>
        <span className="mono muted" style={{ fontSize: 12.5 }}>
          {project.slug}
        </span>
      </div>

      <div className="card__body stack" style={{ gap: 14 }}>
        {problem && <Notice tone="danger">{problem}</Notice>}
        {saved && !dirty && <Notice tone="success">Saved.</Notice>}

        <Field label="Name" required htmlFor="ps-name">
          <input
            id="ps-name"
            className="input"
            value={name}
            required
            disabled={!editable}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field label="Description" htmlFor="ps-description">
          <input
            id="ps-description"
            className="input"
            value={description}
            disabled={!editable}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <div className="checkbox-row">
          <input
            id="ps-pii"
            type="checkbox"
            checked={isPii}
            disabled={!editable}
            onChange={(event) => setIsPii(event.target.checked)}
          />
          <label htmlFor="ps-pii">
            <span className="field__label">Holds customer data</span>
            <div className="field__hint">
              Every download asks for a reason, recorded against the person who asked.
            </div>
          </label>
        </div>

        <div className="checkbox-row">
          <input
            id="ps-notify"
            type="checkbox"
            checked={notifications}
            disabled={!editable}
            onChange={(event) => setNotifications(event.target.checked)}
          />
          <label htmlFor="ps-notify">
            <span className="field__label">Send notifications</span>
            <div className="field__hint">
              Turning this off silences every channel for this project, whatever a
              manifest asks for.
            </div>
          </label>
        </div>

        <div className="row" style={{ gap: 16, alignItems: "flex-start" }}>
          <Field
            label="Artifact retention"
            hint="Days. The record of each run outlives its files."
            htmlFor="ps-retention"
          >
            <input
              id="ps-retention"
              className="input"
              type="number"
              min={1}
              max={3650}
              style={{ width: 110 }}
              value={retention}
              disabled={!editable}
              onChange={(event) => setRetention(Number(event.target.value))}
            />
          </Field>

          <Field
            label="Concurrent runs"
            hint="Stops one project consuming every slot."
            htmlFor="ps-concurrent"
          >
            <input
              id="ps-concurrent"
              className="input"
              type="number"
              min={1}
              max={100}
              style={{ width: 110 }}
              value={concurrent}
              disabled={!editable}
              onChange={(event) => setConcurrent(Number(event.target.value))}
            />
          </Field>

          <Field label="Schedules" hint="Maximum for this project." htmlFor="ps-schedules">
            <input
              id="ps-schedules"
              className="input"
              type="number"
              min={0}
              max={500}
              style={{ width: 110 }}
              value={schedules}
              disabled={!editable}
              onChange={(event) => setSchedules(Number(event.target.value))}
            />
          </Field>
        </div>

        {editable ? (
          <div className="row">
            <button type="submit" className="button button--primary" disabled={busy || !dirty}>
              {busy ? "Saving…" : "Save changes"}
            </button>
            {dirty && <span className="muted" style={{ fontSize: 12.5 }}>Unsaved changes</span>}
          </div>
        ) : (
          <div className="faint" style={{ fontSize: 12.5 }}>
            Only a project administrator can change these.
          </div>
        )}
      </div>
    </form>
  );
}

export function Settings() {
  const { can } = useProject();

  return (
    <div className="stack" style={{ gap: 18 }}>
      <ProjectSettings />
      {can("viewer") && <Members />}
      {can("viewer") && <Secrets />}
      <AuditTrail />
    </div>
  );
}

